import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Minus, Truck, Sparkles } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";
import { fetchProduct, fetchProducts, formatPrice } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImage, setActiveImage] = useState(0);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setActiveImage(0);
    setSize("");
    setQty(1);
    fetchProduct(slug).then((p) => {
      setProduct(p);
      setColor(p.colors?.[0]?.value || "");
    });
    fetchProducts().then((all) => {
      setRelated(all.filter((p) => p.slug !== slug).slice(0, 3));
    });
  }, [slug]);

  if (!product) {
    return <div className="pt-40 text-center font-body text-sm opacity-60">…</div>;
  }

  const name = product.name?.[lang] || product.name?.en;
  const tagline = product.tagline?.[lang] || product.tagline?.en;
  const description = product.description?.[lang] || product.description?.en;
  const materials = product.materials?.[lang] || product.materials?.en;
  const category = product.category?.[lang] || product.category?.en;

  const handleAdd = () => {
    if (product.sizes?.length && !size) {
      toast.error(t.common.selectSize);
      return;
    }
    addItem(product, { size, color, quantity: qty });
    toast.success(t.common.addedToCart);
  };

  return (
    <div data-testid="product-detail-page" className="pt-28 lg:pt-32 pb-24 bg-maly-cream">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <Link to="/shop" data-testid="back-to-shop" className="inline-flex items-center gap-2 font-body text-[11px] tracking-[0.25em] uppercase opacity-60 hover:opacity-100 mb-10">
          <ArrowLeft size={14} /> {t.common.back}
        </Link>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20">
          {/* Image gallery */}
          <div className="flex gap-3 lg:gap-5">
            {/* Thumbnails */}
            <div className="hidden sm:flex flex-col gap-3 w-20 lg:w-24">
              {product.images?.map((img, i) => (
                <button
                  key={i}
                  data-testid={`thumb-${i}`}
                  onClick={() => setActiveImage(i)}
                  className={`aspect-[3/4] bg-maly-creamDeep overflow-hidden border ${activeImage === i ? "border-maly-navy" : "border-transparent"} hover:border-maly-navy/40 transition`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            {/* Main image */}
            <div className="flex-1 aspect-[3/4] bg-maly-creamDeep overflow-hidden">
              <img src={product.images?.[activeImage]} alt={name} className="w-full h-full object-cover animate-fade-in" />
            </div>
          </div>

          {/* Info */}
          <div className="lg:pl-8 lg:py-4">
            <Reveal>
              <div className="font-body text-[11px] tracking-[0.3em] uppercase text-maly-navy/60 mb-4">{category}</div>
            </Reveal>
            <Reveal delay={100}>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-[-0.01em] leading-[1.02]">{name}</h1>
            </Reveal>
            <Reveal delay={150}>
              <p className="font-serif italic text-lg lg:text-xl opacity-70 mt-3">{tagline}</p>
            </Reveal>
            <Reveal delay={200}>
              <div className="font-display text-2xl mt-8">{formatPrice(product.price, product.currency)}</div>
            </Reveal>

            <Reveal delay={300} className="mt-10 space-y-8">
              {/* Color picker */}
              {product.colors?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="font-body text-[11px] tracking-[0.25em] uppercase opacity-70">{t.common.color}</label>
                    <span className="font-body text-[11px] tracking-[0.15em] capitalize">{product.colors.find(c => c.value === color)?.label}</span>
                  </div>
                  <div className="flex gap-3" data-testid="color-picker">
                    {product.colors.map((c) => (
                      <button
                        key={c.value}
                        data-testid={`color-${c.value}`}
                        onClick={() => setColor(c.value)}
                        className={`w-9 h-9 rounded-full border-2 transition-all duration-300 ${color === c.value ? "border-maly-navy scale-110" : "border-maly-navy/20"}`}
                        style={{ background: c.hex }}
                        title={c.label}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Size picker */}
              {product.sizes?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="font-body text-[11px] tracking-[0.25em] uppercase opacity-70">{t.common.size}</label>
                    <button className="font-body text-[11px] tracking-[0.15em] uppercase opacity-60 link-underline">{t.common.sizeGuide}</button>
                  </div>
                  <div className="grid grid-cols-5 gap-2" data-testid="size-picker">
                    {product.sizes.map((s) => (
                      <button
                        key={s}
                        data-testid={`size-${s}`}
                        onClick={() => setSize(s)}
                        className={`py-3 border font-body text-sm tracking-wider transition-all ${
                          size === s
                            ? "bg-maly-navy text-maly-cream border-maly-navy"
                            : "border-maly-navy/20 hover:border-maly-navy"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Qty + add to cart */}
              <div className="flex items-stretch gap-3 pt-2">
                <div className="flex items-center border border-maly-navy/30">
                  <button data-testid="qty-down" onClick={() => setQty(Math.max(1, qty - 1))} className="px-4" aria-label="Decrease"><Minus size={14} /></button>
                  <span className="font-body text-sm w-8 text-center">{qty}</span>
                  <button data-testid="qty-up" onClick={() => setQty(qty + 1)} className="px-4" aria-label="Increase"><Plus size={14} /></button>
                </div>
                <button data-testid="add-to-cart" onClick={handleAdd} className="btn-primary flex-1">
                  {t.common.addToCart}
                </button>
              </div>

              {/* Shipping note */}
              <div className="flex items-start gap-3 pt-4 border-t border-maly-navy/10">
                <Truck size={18} strokeWidth={1.4} className="opacity-60 mt-0.5" />
                <p className="font-body text-xs opacity-60 leading-relaxed">{t.common.shippingText}</p>
              </div>
            </Reveal>

            {/* Details accordion */}
            <Reveal delay={400} className="mt-12 space-y-6">
              <div className="border-t border-maly-navy/10 pt-6">
                <div className="font-body text-[11px] tracking-[0.3em] uppercase opacity-70 mb-3">{t.common.details}</div>
                <p className="font-body text-sm opacity-75 leading-[1.8]">{description}</p>
              </div>
              {materials && (
                <div className="border-t border-maly-navy/10 pt-6">
                  <div className="font-body text-[11px] tracking-[0.3em] uppercase opacity-70 mb-3">{t.common.materials}</div>
                  <p className="font-body text-sm opacity-75 leading-[1.8]">{materials}</p>
                </div>
              )}
            </Reveal>
          </div>
        </div>

        {/* Complete the look */}
        {related.length > 0 && (
          <section className="mt-32 lg:mt-48">
            <div className="flex items-end justify-between mb-12">
              <h2 className="font-display text-3xl lg:text-4xl font-light flex items-center gap-3">
                <Sparkles size={18} strokeWidth={1.4} className="text-maly-gold" />
                {t.common.youMayAlsoLike}
              </h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
              {related.map((p, i) => (
                <Reveal key={p.id} delay={i * 80}>
                  <ProductCard product={p} />
                </Reveal>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
